/**
 * Uploads one file straight to S3 with a signed PUT URL from the API.
 *
 * Uses XMLHttpRequest rather than axios: axios here carries a global base URL
 * and `x-client-id` header for the Enki API, and neither belongs on a request
 * to S3. XMLHttpRequest also reports upload progress.
 */
export class S3UploadError extends Error {
  // null when the request never completed, for example a dropped connection
  readonly status: number | null;

  constructor(message: string, status: number | null) {
    super(message);
    this.name = 'S3UploadError';
    this.status = status;
  }
}

export function uploadToS3({
  url,
  contentType,
  file,
  onProgress,
}: {
  url: string;
  contentType: string;
  file: File;
  onProgress?: (fraction: number) => void;
}): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('PUT', url);
    // The URL's signature covers the exact file size, which the browser sends
    // as Content-Length; S3 rejects the upload if the file changed.
    request.setRequestHeader('Content-Type', contentType);
    request.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(event.loaded / event.total);
      }
    };
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        resolve();
      } else {
        reject(
          new S3UploadError(
            `Upload failed with status ${request.status}`,
            request.status
          )
        );
      }
    };
    request.onerror = () =>
      reject(new S3UploadError('Upload failed: network error', null));
    request.onabort = () =>
      reject(new S3UploadError('Upload was cancelled', null));
    request.send(file);
  });
}
